import React, { useState, useEffect } from 'react';
import { Eye, MessageSquare, Clock, CheckCircle, XCircle, AlertTriangle, Search, Filter, FileText, Play, Volume2, Image, Layers, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import Table, { ActionButton, ActionMenu, StatusBadge } from '../../components/common/Table';
import Modal, { ConfirmModal } from '../../components/common/Modal';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

/**
 * Submissions Management Page
 * 
 * Comprehensive user submission management interface for administrators.
 * Allows viewing, updating status, and managing user-submitted reports and issues.
 */
const SubmissionsPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    search: '',
    user_id: '',
    watershed_id: '',
    site_id: '',
    point_id: '',
    observer_name: '',
    report_type: '',
    start_date: '',
    end_date: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  const columns = [
    {
      key: 'id',
      title: 'Submission',
      sortable: true,
      render: (submission) => (
        <div className="flex items-center">
          <div className={`p-2 rounded-lg mr-3 bg-blue-100 text-blue-600`}>
            <FileText className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium text-secondary-900">
              {submission.title || submission.description?.substring(0, 50) || 'Untitled'}
            </div>
            <div className="text-sm text-secondary-500">
              ID: {submission.id.slice(0, 8)}... • {submission.report_type || 'General'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'observer_name',
      title: 'Observer',
      className: 'hidden md:table-cell',
      render: (submission) => (
        <div className="font-medium text-secondary-900">{submission.observer_name || 'Unknown'}</div>
      ),
    },
    {
      key: 'categories',
      title: 'Categories',
      sortable: false,
      className: 'hidden md:table-cell',
      render: (submission) => {
        if (submission.categories && submission.categories.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {submission.categories.slice(0, 2).map((category, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  {category}
                </span>
              ))}
              {submission.categories.length > 2 && (
                <span className="text-xs text-secondary-500">+{submission.categories.length - 2}</span>
              )}
            </div>
          );
        }
        return <span className="text-secondary-400 text-sm">No categories</span>;
      },
    },
    {
      key: 'observation_date',
      title: 'Date',
      sortable: true,
      render: (submission) => {
        const date = submission.observation_date ? new Date(submission.observation_date) : new Date(submission.created_at);
        return (
          <div className="font-medium text-secondary-900">
            {date.toLocaleDateString()}
            <div className="text-xs text-secondary-500">{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (submission) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewSubmission(submission)}
          />
          <ActionMenu
            item={submission}
            actions={[
              {
                key: 'status',
                label: 'Update Status',
                icon: MessageSquare,
                onClick: handleUpdateStatus,
              },
              {
                key: 'delete',
                label: 'Delete Submission',
                icon: XCircle,
                variant: 'danger',
                onClick: handleDeleteSubmission,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadSubmissions();
  }, [filters, sortConfig]);

  const loadSubmissions = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getSubmissions(params);

      console.log('Submissions response:', response);
      
      if (response.success) {
        setSubmissions(response.data.submissions || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load submissions');
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get severity color
  const getSeverityColor = (severity) => {
    const colors = {
      'Low': 'bg-green-100 text-green-600',
      'Medium': 'bg-yellow-100 text-yellow-600',
      'High': 'bg-red-100 text-red-600',
    };
    return colors[severity] || 'bg-gray-100 text-gray-600';
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewSubmission = (submission) => {
    console.log(
      "Plot Name: ", submission.plot?.name,
      "Plot ID: ", submission.plot?.id,
      "Submission ID: ", submission.id,
      "Farm ID: ", submission.farm_id,
      "Bed ID: ", submission.bed_id,
      "Bed Name: ", submission.bed?.name,
      "Bed Size: ", submission.bed?.size,
      "Seed: ", submission.bed?.seed,
      "Submisison", submission
    );
    setSelectedSubmission(submission);
    setShowViewModal(true);
  };

  const handleUpdateStatus = (submission) => {
    setSelectedSubmission(submission);
    setNewStatus(submission.status);
    setStatusNote('');
    setShowStatusModal(true);
  };

  const handleDeleteSubmission = (submission) => {
    setSelectedSubmission(submission);
    setShowDeleteModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedSubmission) return;

    try {
      setStatusLoading(true);
      
      const response = await apiService.updateSubmissionStatus(selectedSubmission.id, {
        status: newStatus,
        note: statusNote,
      });
      
      if (response.success) {
        toast.success('Submission status updated successfully');
        setShowStatusModal(false);
        loadSubmissions();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubmission) return;

    try {
      const response = await apiService.deleteSubmission(selectedSubmission.id);
      
      if (response.success) {
        toast.success('Submission deleted successfully');
        setShowDeleteModal(false);
        loadSubmissions();
      } else {
        toast.error(response.message || 'Failed to delete submission');
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      {/* <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">User Submissions</h1>
          <p className="text-secondary-600">Manage user reports, issues, and feedback submissions</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span>Unhealthy: {submissions.filter(s => s.plant_conditions?.Unhealthy).length}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>Healthy: {submissions.filter(s => s.plant_conditions?.Healthy).length}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm text-secondary-600">
            <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            <span>Submitted: {submissions.filter(s => s.status === 'submitted').length}</span>
          </div>
        </div>
      </div> */}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
        {/* Filter Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
            <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
          </div>
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
          >
            <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
            {isFiltersCollapsed ? (
              <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
            ) : (
              <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
            )}
          </button>
        </div>

        {/* Collapsible Filter Content */}
        {!isFiltersCollapsed && (
          <div className="p-3 md:p-6">
            <div className="space-y-3 md:space-y-4">
              {/* First Row - Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search submissions..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                />
              </div>

              {/* Second Row - Observer and Farm */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Observer name..."
                  value={filters.observer_name}
                  onChange={(e) => handleFilterChange('observer_name', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
                <input
                  type="text"
                  placeholder="Farm ID..."
                  value={filters.farm_id}
                  onChange={(e) => handleFilterChange('farm_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
              </div>

              {/* Third Row - User and Plot */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="User ID..."
                  value={filters.user_id}
                  onChange={(e) => handleFilterChange('user_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
                <input
                  type="text"
                  placeholder="Plot ID..."
                  value={filters.plot_id}
                  onChange={(e) => handleFilterChange('plot_id', e.target.value)}
                  className="form-input w-full text-sm md:text-base"
                />
              </div>

              {/* Fourth Row - Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="date"
                  placeholder="Start date"
                  value={filters.start_date ?
                    `${filters.start_date.slice(0,4)}-${filters.start_date.slice(4,6)}-${filters.start_date.slice(6,8)}` :
                    ''
                  }
                  onChange={(e) => handleFilterChange('start_date', e.target.value.replace(/-/g, ''))}
                  className="form-input w-full text-sm md:text-base"
                  title="Start date filter"
                />
                <input
                  type="date"
                  placeholder="End date"
                  value={filters.end_date ?
                    `${filters.end_date.slice(0,4)}-${filters.end_date.slice(4,6)}-${filters.end_date.slice(6,8)}` :
                    ''
                  }
                  onChange={(e) => handleFilterChange('end_date', e.target.value.replace(/-/g, ''))}
                  className="form-input w-full text-sm md:text-base"
                  title="End date filter"
                />
              </div>

              <div className="flex justify-center md:justify-end mt-4">
                {/* Clear Filters */}
                <button
                  onClick={() => setFilters({ search: '', user_id: '', farm_id: '', observer_name: '', plot_id: '', start_date: '', end_date: '', page: 1, limit: 20 })}
                  className="btn btn-secondary text-sm md:text-base px-4 py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Submissions Table - Desktop */}
      <div className="hidden md:flex md:flex-col md:h-[calc(100vh-200px)] md:min-h-[500px]">
        <Table
          columns={columns}
          data={submissions}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          emptyMessage="No submissions found"
          className="flex-1 flex flex-col"
        />
      </div>

      {/* Submissions Cards - Mobile */}
      <div className="md:hidden space-y-3 px-2">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : submissions.length > 0 ? (
          submissions.map((submission) => (
            <div key={submission.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between mb-3 w-full">
                <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                  <div className={`p-1.5 rounded-md flex-shrink-0 ${getSeverityColor(submission.severity)}`}>
                    <FileText className="w-3 h-3" />
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {submission.title || submission.description?.substring(0, 50) || 'Untitled'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {submission.report_type} • ID: {submission.id.slice(0, 8)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1 flex-shrink-0">
                  <ActionButton
                    icon={Eye}
                    label="View Details"
                    onClick={() => handleViewSubmission(submission)}
                    size="sm"
                    className="p-1"
                  />
                  <ActionMenu
                    item={submission}
                    actions={[
                      {
                        key: 'status',
                        label: 'Update Status',
                        icon: MessageSquare,
                        onClick: handleUpdateStatus,
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        icon: XCircle,
                        variant: 'danger',
                        onClick: handleDeleteSubmission,
                      },
                    ]}
                  />
                </div>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-2 text-xs w-full">
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Observer</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {submission.observer_name || 'Unknown'}
                  </div>
                  {submission.categories && submission.categories.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {submission.categories.slice(0, 2).map((category, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                        >
                          {category}
                        </span>
                      ))}
                      {submission.categories.length > 2 && (
                        <span className="text-xs text-gray-500">+{submission.categories.length - 2} more</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="min-w-0 overflow-hidden">
                  <div className="text-gray-500 text-xs uppercase tracking-wide">Date</div>
                  <div className="font-medium text-gray-900 text-xs truncate">
                    {submission.observation_date ? new Date(submission.observation_date).toLocaleDateString() : new Date(submission.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap items-center gap-1 mt-2 pt-2 border-t border-gray-100 w-full">
                {submission.categories && submission.categories.length > 0 && submission.categories.map((category, index) => (
                  <StatusBadge
                    key={index}
                    status={category}
                    variant="info"
                  />
                ))}
                {submission.severity && (
                  <StatusBadge
                    status={submission.severity}
                    variant={submission.severity === 'High' ? 'danger' : submission.severity === 'Medium' ? 'warning' : 'success'}
                  />
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No submissions found</p>
          </div>
        )}

        {/* Mobile Pagination */}
        {pagination && pagination.totalPages > 0 && (
          <div className="bg-white rounded-lg shadow p-3 mx-2 w-[calc(100vw-1rem)] max-w-full">
            <div className="text-xs text-gray-700 text-center mb-3">
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} results
            </div>
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrev}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed p-2"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-xs text-gray-700 px-2 whitespace-nowrap">
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNext}
                className="btn btn-secondary btn-sm disabled:opacity-50 disabled:cursor-not-allowed p-2"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Submission Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="Last Update"
        size="xl"
      >
        {selectedSubmission && (
          <div className="space-y-4 md:space-y-6 pb-4">
            

            {/* Submission Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 md:p-6 border border-blue-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {selectedSubmission.title || 'Observation Report'}
                  </h3>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedSubmission.severity)}`}>
                      {selectedSubmission.severity || 'Not Set'}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedSubmission.report_type || 'General'}
                    </span>
                    {selectedSubmission.priority && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        Priority: {selectedSubmission.priority}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information - River Monitoring */}
            {(selectedSubmission.watershed_id || selectedSubmission.site_id || selectedSubmission.point_id) && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Watershed Info */}
                {selectedSubmission.watershed_id && (
                  <div className="bg-blue-50 rounded-lg p-3 md:p-4 border border-blue-200">
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                      <h4 className="text-sm font-semibold text-blue-800">Watershed</h4>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-blue-600">
                        ID: {selectedSubmission.watershed_id}
                      </div>
                    </div>
                  </div>
                )}

                {/* Site Info */}
                {selectedSubmission.site_id && (
                  <div className="bg-green-50 rounded-lg p-3 md:p-4 border border-green-200">
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <h4 className="text-sm font-semibold text-green-800">Site</h4>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-green-600">
                        ID: {selectedSubmission.site_id}
                      </div>
                    </div>
                  </div>
                )}

                {/* Point Info */}
                {selectedSubmission.point_id && (
                  <div className="bg-amber-50 rounded-lg p-3 md:p-4 border border-amber-200">
                    <div className="flex items-center mb-2">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mr-2"></div>
                      <h4 className="text-sm font-semibold text-amber-800">Point</h4>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs text-amber-600">
                        ID: {selectedSubmission.point_id}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Observer & Submission Info */}
            <div className="bg-gray-50 rounded-lg p-3 md:p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-1">Observer</div>
                  <div className="font-medium text-gray-900">
                    {selectedSubmission.observer_name || 'Unknown'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-1">Observation Date</div>
                  <div className="font-medium text-gray-900">
                    {selectedSubmission.observation_date
                      ? new Date(selectedSubmission.observation_date).toLocaleString()
                      : new Date(selectedSubmission.created_at).toLocaleString()}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-xs font-medium uppercase tracking-wide mb-1">Submission ID</div>
                  <div className="font-medium text-gray-900 text-xs">
                    {selectedSubmission.id.slice(0, 12)}...
                  </div>
                </div>
              </div>
            </div>

            {/* Categories */}
            {selectedSubmission.categories && selectedSubmission.categories.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Categories</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <div className="flex flex-wrap gap-2">
                    {selectedSubmission.categories.map((category, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Custom Tags */}
            {selectedSubmission.custom_tags && selectedSubmission.custom_tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Custom Tags</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <div className="flex flex-wrap gap-2">
                    {selectedSubmission.custom_tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Observation Description */}
            {selectedSubmission.description && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Description</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <p className="text-secondary-900 whitespace-pre-wrap text-sm">{selectedSubmission.description}</p>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            {selectedSubmission.additional_notes && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">Additional Notes</label>
                <div className="bg-white border rounded-lg p-3 md:p-4">
                  <p className="text-secondary-900 whitespace-pre-wrap text-sm">{selectedSubmission.additional_notes}</p>
                </div>
              </div>
            )}


            {/* Uploaded Media */}
            {(selectedSubmission.media_urls || selectedSubmission.audio_url) && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-3 md:mb-4">Uploaded Media</label>

                {/* Photos & Videos from media_urls */}
                {selectedSubmission.media_urls && selectedSubmission.media_urls.length > 0 && (
                  <div className="mb-4 md:mb-6">
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 md:mb-3 flex items-center">
                      <Layers className="w-4 h-4 mr-2" />
                      Photos & Videos ({selectedSubmission.media_urls.length})
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-4">
                      {selectedSubmission.media_urls.map((mediaUrl, index) => (
                        <div
                          key={index}
                          className="relative group cursor-pointer"
                          onClick={() => window.open(mediaUrl, '_blank')}
                        >
                          {mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                            <img
                              src={mediaUrl}
                              alt={`Media ${index + 1}`}
                              className="w-full h-24 md:h-32 object-cover rounded-lg hover:opacity-90 transition-opacity"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgZmlsbD0iI2Y5ZmFmYiIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjEyOCIgZmlsbD0iI2Y5ZmFmYiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE0IiBmb250LWZhbWlseT0iQXJpYWwiIGZpbGw9IiM2YjczODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIwLjNlbSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pjwvc3ZnPg==';
                              }}
                            />
                          ) : mediaUrl.match(/\.(mp4|webm|mov|avi)$/i) ? (
                            <div className="w-full h-24 md:h-32 bg-gray-900 rounded-lg flex items-center justify-center relative overflow-hidden">
                              <video
                                src={mediaUrl}
                                className="w-full h-full object-cover"
                                muted
                              />
                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                <Play className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          ) : (
                            <div className="w-full h-24 md:h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                            <Eye className="w-5 h-5 md:w-6 md:h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audio Recording */}
                {selectedSubmission.audio_url && (
                  <div className="mb-4 md:mb-6">
                    <h4 className="text-sm font-medium text-secondary-600 mb-2 md:mb-3 flex items-center">
                      <Volume2 className="w-4 h-4 mr-2" />
                      Audio Recording
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <audio controls className="w-full">
                        <source src={selectedSubmission.audio_url} />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title="Update Submission Status"
      >
        {selectedSubmission && (
          <div className="space-y-4 min-h-0">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                New Status
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="form-select w-full"
                required
              >
                <option value="submitted">Submitted</option>
                <option value="reviewed">Reviewed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Status Note (Optional)
              </label>
              <textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                rows={3}
                className="form-input w-full resize-none"
                placeholder="Add a note about this status change..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
                disabled={statusLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className="btn btn-primary"
                disabled={statusLoading}
              >
                {statusLoading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Submission"
        message={`Are you sure you want to delete the submission "${selectedSubmission?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default SubmissionsPage;